import { System } from '@ecs/System';
import { Tag } from '@components/Tag';
import { UIElement } from '@components/UIElement';
import { Entity } from '@ecs/Entity';

export interface GameStats {
  score: number;
  enemiesKilled: number;
}

export class GameStateSystem extends System {
  private gameEndCallback?: (isVictory: boolean) => void;
  private statsProvider: () => GameStats = () => ({ score: 0, enemiesKilled: 0 });
  private ended = false;

  update(_deltaTime: number): void {
    if (this.ended) return;

    // Check if all player ships are destroyed
    const playerShips = this.world.getEntitiesWithComponents(Tag).filter(entity => {
      const tag = entity.getComponent(Tag);
      return tag?.value === 'player';
    });

    if (playerShips.length === 0) {
      // All player ships destroyed - game over (defeat)
      this.endGame(false);
    }
  }

  endGame(isVictory: boolean): void {
    if (this.ended) return;
    this.ended = true;

    const stats = this.statsProvider();

    // Create UI elements for game over screen
    const gameOverUIEntities = [
      new Entity().addComponent(new UIElement('gameOverTitle', isVictory ? 'Mission Complete!' : 'Mission Failed')),
      new Entity().addComponent(new UIElement('gameOverMessage', isVictory ? 'All objectives completed!' : 'All ships destroyed!')),
      new Entity().addComponent(new UIElement('gameOverButton', isVictory ? 'New Mission' : 'Try Again')),
      new Entity().addComponent(new UIElement('gameOverVisible', 'true')),
      new Entity().addComponent(new UIElement('finalScore', stats.score)),
      new Entity().addComponent(new UIElement('enemiesKilled', stats.enemiesKilled))
    ];

    // Add all UI entities to world
    for (const entity of gameOverUIEntities) {
      this.world.addEntity(entity);
    }

    // Notify callback if set
    if (this.gameEndCallback) {
      this.gameEndCallback(isVictory);
    }
  }

  isGameOver(): boolean {
    return this.ended;
  }

  reset(): void {
    this.ended = false;
  }

  /** Ends the round silently, e.g. after the level objective was completed. */
  stop(): void {
    this.ended = true;
  }

  setGameEndCallback(callback: (isVictory: boolean) => void): void {
    this.gameEndCallback = callback;
  }

  setStatsProvider(provider: () => GameStats): void {
    this.statsProvider = provider;
  }
}
